import { ArrowDownUpIcon } from 'lucide-react'
import { UseFormReturn } from 'react-hook-form'

import { SwapFormSchema } from '../logic/form/schema'
import { PageStatus, SwapFormFields } from '../types'
import { ActionsViewState } from '@/features/actions/types'
import { ActionsView } from '@/features/actions/views/ActionsView'
import PanelConnector from '@/ui/assets/spark-logo-panel-connector.svg?react'
import { Button } from '@/ui/atoms/button/Button'
import { Form } from '@/ui/atoms/form/Form'
import { Panel } from '@/ui/atoms/panel/Panel'
import { AssetInput } from '@/ui/organisms/asset-input/AssetInput'
import { cn } from '@/ui/utils/style'

export interface SwapPanelProps {
  formFields: SwapFormFields
  form: UseFormReturn<SwapFormSchema>
  pageStatus: PageStatus
  actionsViewState: ActionsViewState
  isConnected: boolean
  openConnectModal: () => void
  className?: string
}

export function SwapPanel({
  formFields,
  form,
  pageStatus,
  actionsViewState,
  isConnected,
  openConnectModal,
  className,
}: SwapPanelProps) {
  const {
    selectedAssetIn,
    selectedAssetInBalance,
    selectedAssetOut,
    changeAssetIn,
    changeAssetOut,
    swapAssets,
    assetInOptions,
    assetOutOptions,
    maxSelectedFieldName,
  } = formFields

  return (
    <Form {...form}>
      <div className={cn('relative flex flex-col rounded-md bg-tertiary p-1.5', className)}>
        <div className="flex flex-col gap-2 rounded-sm bg-primary p-3 sm:gap-4 sm:p-8">
          <div className="typography-label-1">From</div>
          <AssetInput
            fieldName="amount"
            control={form.control}
            selectorAssets={assetInOptions}
            selectedAsset={selectedAssetIn}
            setSelectedAsset={changeAssetIn}
            maxSelectedFieldName={maxSelectedFieldName}
            maxValue={selectedAssetInBalance}
            showUsdValue={false}
            indicateZeroBalance
            showError
          />
        </div>
        <div className="relative my-[3px]">
          <div className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 flex items-center justify-center">
            <button
              onClick={swapAssets}
              className={cn(
                'absolute z-10 ml-[7px] rounded-full bg-primary p-px sm:ml-2.5 sm:p-[2px]',
                'opacity-0 hover:opacity-100',
              )}
            >
              <ArrowDownUpIcon className="sm:icon-size-sm icon-size-xs text-farms-600" />
            </button>
            <PanelConnector className="w-[48px] rotate-90 transition-opacity duration-200 sm:w-[68px]" />
          </div>
        </div>
        <div className="flex flex-col gap-2 rounded-sm bg-primary p-3 sm:gap-4 sm:p-8">
          <div className="typography-label-1">To</div>
          <AssetInput
            fieldName="amount"
            control={form.control}
            selectorAssets={assetOutOptions}
            selectedAsset={selectedAssetOut}
            setSelectedAsset={changeAssetOut}
            maxSelectedFieldName={maxSelectedFieldName}
            maxValue={selectedAssetInBalance}
            showError={false}
            showUsdValue={false}
          />
        </div>
        {isConnected ? (
          <>
            {pageStatus.state === 'form' && (
              <Button
                type="submit"
                className="mt-1.5 w-full"
                size="l"
                disabled={!pageStatus.actionsEnabled}
                onClick={pageStatus.submitForm}
              >
                Swap
              </Button>
            )}
            {pageStatus.state === 'confirmation' && (
              <Panel className="mt-1.5">
                <ActionsView
                  state={actionsViewState}
                  actionsGridLayout="compact"
                  allowChangeWhenActionsInProgress={false}
                />
              </Panel>
            )}
          </>
        ) : (
          <Button type="button" className="mt-1.5 w-full" size="l" onClick={openConnectModal}>
            Connect Wallet
          </Button>
        )}
      </div>
    </Form>
  )
}
